// noinspection JSIgnoredPromiseFromCall

import { NavigateOptions, useNavigate, useSearch } from '@tanstack/react-router';
import { isEmpty, isNumeric } from '@/lib/utils.ts';

function cleanSearchParams<T extends Record<string, unknown>>(search: T) {
  const newSearch: any = { ...search };
  Object.keys(newSearch).forEach((key) => {
    const value: unknown = newSearch[key];
    if (value === undefined || value === '' || (typeof value === 'number' && isNaN(value))) {
      delete newSearch[key];
    } else if (isNumeric(value)) {
      newSearch[key] = Number(value);
    }
  });
  if (search.pageIndex === 0) delete newSearch.pageIndex;
  if (search.pageSize === 24) delete newSearch.pageSize;

  return newSearch as Partial<T>;
}

export type TPaginationParams = { pageIndex?: number; pageSize?: number; sortBy?: `${string}.${'asc' | 'desc'}` };
export type TSearchParams<T> = T & TPaginationParams;

// noinspection JSUnusedGlobalSymbols
export function getValidateSearchType<T = object>() {
  return () => ({}) as TSearchParams<T>;
}

export type TUseSearchParams<T = any> = {
  params: any;
  setParams: (partialParams: any) => Promise<void> | void;
  resetParams: () => Promise<void> | void;
  removeValue: (param: string, value: any) => void;
  addValue: (param: string, value: any) => void;
  has(param: keyof TSearchParams<T>): boolean;
  get(param: string): any;
  set(param: string, value: any): void;
};

export function useSearchParams<T>(): TUseSearchParams<T> {
  const navigate = useNavigate();
  const params = useSearch({ strict: false }) as any;

  const setParams = (partialParams: any, navigateOpts: NavigateOptions = {}) => {
    if (!('start' in partialParams)) {
      partialParams['start'] = undefined;
    }
    const cleaned = cleanSearchParams({
      ...params,
      ...partialParams,
    });
    // console.trace('setParams', JSON.stringify({partialParams, params, cleaned}), JSON.stringify(window.location));
    return navigate({
      ...navigateOpts,
      search: cleaned,
    });
  };

  const resetParams = () => {
    return navigate({ search: {} as any });
  };

  const has = (param: string) => params && param in params;

  const get = (param: string) => {
    if (params && param in params) {
      return params[param];
    }
    return undefined;
  };

  const set = (param: string, value: any) => {
    const newParams = { [param]: value };
    setParams(newParams);
  };

  const addValue = (param: string, value: any) => {
    const currentValue = String(get(param) || '');
    const values = isEmpty(currentValue) ? [] : currentValue.split(',');
    if (values.includes(String(value))) {
      return;
    }
    values.push(String(value));
    const newValue = values.join(',');
    const newParams = { [param]: newValue };
    setParams(newParams);
  };

  const removeValue = (param: string, value: any) => {
    const currentValue = String(get(param) || '');
    const values = currentValue.split(',').filter((v) => v !== String(value));
    const newValue = values.join(',');
    const newParams: any = {};
    if (newValue === '') {
      newParams[param] = undefined;
    } else {
      newParams[param] = newValue;
    }
    setParams(newParams);
  };

  return {
    params,
    setParams,
    resetParams,
    has,
    get,
    set,
    addValue,
    removeValue,
  } as TUseSearchParams<T>;
}
